import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { PageHeader, Modal } from "../components/Crud";
import DatePicker, { DateTimePicker } from "../components/DatePicker";
import ReminderEditor from "../components/ReminderEditor";
import { usePrefs, useFormat } from "../contexts/PrefsContext";
import {
    Plus, Pencil, Trash2, Copy, CheckCircle2, MapPin, AlignLeft,
    Calendar as CalIcon, Clock, X, Palette,
} from "lucide-react";

/* ------------------------------------------------------------------ helpers */
function ymdLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function dayDiff(aYmd, bYmd) {
    const [ay, am, ad] = aYmd.split("-").map(Number);
    const [by, bm, bd] = bYmd.split("-").map(Number);
    const a = new Date(ay, am - 1, ad), b = new Date(by, bm - 1, bd);
    return Math.round((b - a) / 86_400_000);
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d, weekStart) {
    const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = (x.getDay() - weekStart + 7) % 7;
    x.setDate(x.getDate() - diff);
    return x;
}

const CATEGORIES = [
    { key: "appointment", label: "Appointment", color: "var(--brand-purple)",  dot: "🟣" },
    { key: "meeting",     label: "Meeting",     color: "#0EA5E9",              dot: "🔵" },
    { key: "deadline",    label: "Deadline",    color: "var(--brand-magenta)", dot: "🔴" },
    { key: "personal",    label: "Personal",    color: "#16A34A",              dot: "🟢" },
    { key: "class",       label: "Class",       color: "var(--brand-orange)",  dot: "🟠" },
    { key: "other",       label: "Other",       color: "var(--text-ink)",      dot: "⚫" },
];
const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));
const PALETTE = [
    "#7B2D8E", "#FF6B35", "#E91E63", "#0EA5E9", "#16A34A",
    "#EAB308", "#DC2626", "#0F172A",
];

const blankForm = () => ({
    id: "", title: "", description: "", notes: "",
    start: "", end: "", all_day: false,
    location: "", category: "appointment", color: "",
    status: "active",
});

function eventColor(ev) {
    if (ev.color) return ev.color;
    if (ev.kind === "assessment") return "var(--brand-magenta)";
    if (ev.kind === "class") return "var(--brand-orange)";
    return CATEGORY_BY_KEY[ev.category]?.color || "var(--brand-purple)";
}

/* ------------------------------------------------------------------ page */
export default function CalendarPage() {
    const { t } = useTranslation();
    const { prefs } = usePrefs();
    const fmt = useFormat();
    const weekStart = prefs.week_start ?? 1;

    const [events, setEvents] = useState([]);
    const [assessments, setAssessments] = useState([]);
    const [classes, setClasses] = useState([]);
    const [view, setView] = useState("month");
    const [cursor, setCursor] = useState(new Date());
    const [editorOpen, setEditorOpen] = useState(false);
    const [detailsId, setDetailsId] = useState(null);
    const [form, setForm] = useState(blankForm());

    const load = useCallback(async () => {
        const [evRes, assRes, clRes] = await Promise.all([
            api.get("/events").catch(() => ({ data: [] })),
            api.get("/assessments").catch(() => ({ data: [] })),
            api.get("/classes").catch(() => ({ data: [] })),
        ]);
        setEvents(evRes.data || []);
        setAssessments(assRes.data || []);
        setClasses(clRes.data || []);
    }, []);
    useEffect(() => { load(); }, [load]);

    const classById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);
    const assessmentItems = useMemo(() => assessments
        .filter(a => a.due && !a.archived)
        .map(a => ({
            id: `ass-${a.id}`, kind: "assessment",
            title: a.title + (a.class_id && classById[a.class_id] ? ` · ${classById[a.class_id].code || classById[a.class_id].name}` : ""),
            start: `${a.due}T23:59`, end: `${a.due}T23:59`,
            link_to: a.class_id ? `/classes/${a.class_id}` : "/projects",
            description: "Assessment",
            category: "deadline",
        })), [assessments, classById]);

    const allItems = useMemo(() =>
        [...events.map(e => ({ ...e, kind: e.kind || "appointment" })), ...assessmentItems]
            .sort((a, b) => (a.start || "").localeCompare(b.start || "")),
        [events, assessmentItems]);

    // Index events by every Ymd they touch so multi-day events appear on each day.
    // segment.position is 'only' | 'first' | 'mid' | 'last' for continuous-bar styling.
    const eventsByDay = useMemo(() => {
        const map = {};
        for (const ev of allItems) {
            if (!ev.start) continue;
            const sYmd = ev.start.slice(0, 10);
            const eYmd = (ev.end || ev.start).slice(0, 10);
            const span = Math.max(0, dayDiff(sYmd, eYmd));
            for (let i = 0; i <= span; i++) {
                const dY = ymdLocal(addDays(new Date(sYmd + "T12:00:00"), i));
                const pos = span === 0 ? "only" : i === 0 ? "first" : i === span ? "last" : "mid";
                (map[dY] = map[dY] || []).push({ ...ev, _segment: pos, _segIndex: i, _spanDays: span + 1 });
            }
        }
        return map;
    }, [allItems]);

    const detailsEvent = useMemo(() => {
        if (!detailsId) return null;
        return allItems.find(e => e.id === detailsId) || null;
    }, [detailsId, allItems]);

    /* CRUD */
    const openCreate = (dayYmd) => {
        const start = dayYmd ? `${dayYmd}T09:00` : "";
        setForm({ ...blankForm(), start });
        setEditorOpen(true);
    };
    const openEdit = (ev) => {
        setForm({ ...blankForm(), ...ev });
        setDetailsId(null);
        setEditorOpen(true);
    };
    const submit = async (e) => {
        e.preventDefault();
        const payload = { ...form };
        delete payload.id;
        // Reminders are managed by a dedicated endpoint — strip from event body.
        const reminders = payload.reminders || [];
        delete payload.reminders;
        let eventId = form.id;
        if (form.id) {
            await api.put(`/events/${form.id}`, payload);
        } else {
            const { data } = await api.post("/events", payload);
            eventId = data.id;
        }
        // Sync reminder deliveries (best-effort — errors don't block the save).
        if (eventId) {
            try {
                await api.post(`/events/${eventId}/reminders/sync`, { reminders });
            } catch (err) {
                console.warn("Reminder sync failed:", err);
            }
        }
        setEditorOpen(false);
        await load();
    };
    const remove = async (id) => {
        if (!id || id.startsWith("ass-")) return;
        if (!window.confirm("Delete this event?")) return;
        await api.delete(`/events/${id}`);
        setDetailsId(null);
        await load();
    };
    const duplicate = async (ev) => {
        const payload = { ...ev, title: `${ev.title} (copy)` };
        ["id", "_id", "user_id", "created_at", "updated_at", "_segment", "_segIndex", "_spanDays"].forEach(k => delete payload[k]);
        await api.post("/events", payload);
        setDetailsId(null);
        await load();
    };
    const toggleComplete = async (ev) => {
        const next = ev.status === "complete" ? "active" : "complete";
        await api.put(`/events/${ev.id}`, { status: next });
        await load();
    };

    /* Month grid */
    const monthCells = useMemo(() => {
        const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const offset = (first.getDay() - weekStart + 7) % 7;
        const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
        const cells = [];
        for (let i = 0; i < offset; i++) cells.push(addDays(first, -offset + i));
        for (let d = 1; d <= last; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
        while (cells.length % 7 !== 0) cells.push(addDays(cells[cells.length - 1], 1));
        return cells;
    }, [cursor, weekStart]);

    const weekDays = useMemo(() => {
        const ws = startOfWeek(cursor, weekStart);
        return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    }, [cursor, weekStart]);

    const dowHeaders = useMemo(() => {
        const base = new Date(2024, 0, 1); // a Monday
        const out = [];
        for (let i = 0; i < 7; i++) {
            const d = addDays(base, (i + (weekStart - 1) + 7) % 7);
            out.push(d.toLocaleDateString(fmt.locale, { weekday: "short" }));
        }
        return out;
    }, [weekStart, fmt.locale]);

    return (
        <div data-testid="calendar-page">
            <PageHeader
                title={t("pages.calendarTitle")}
                subtitle={t("pages.calendarEyebrow")}
                action={
                    <button onClick={() => openCreate(ymdLocal(new Date()))}
                            className="btn-primary inline-flex items-center gap-2"
                            data-testid="add-event-btn"><Plus size={14}/> {t("pages.calendarAdd")}</button>
                } />

            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                        className="btn-ghost text-sm" data-testid="cal-prev">←</button>
                <div className="font-serif text-2xl px-2" data-testid="cal-title">
                    {cursor.toLocaleDateString(fmt.locale, { month: "long", year: "numeric" })}
                </div>
                <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                        className="btn-ghost text-sm" data-testid="cal-next">→</button>
                <button onClick={() => setCursor(new Date())} className="btn-ghost text-xs" data-testid="cal-today">Today</button>
                <div className="flex-1" />
                {["month", "week", "agenda"].map(v => (
                    <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                            className={`text-sm px-3 py-1.5 rounded-full ${view === v ? "brand-gradient text-white" : "btn-ghost"}`}>{v}</button>
                ))}
            </div>

            {view === "agenda" && (
                <AgendaView allItems={allItems} fmt={fmt}
                            onOpen={(ev) => setDetailsId(ev.id)} />
            )}
            {view === "week" && (
                <WeekView days={weekDays} eventsByDay={eventsByDay}
                          dowHeaders={dowHeaders}
                          fmt={fmt}
                          onOpenEvent={(ev) => setDetailsId(ev.id)}
                          onCreateOn={(yKey) => openCreate(yKey)} />
            )}
            {view === "month" && (
                <MonthView cursor={cursor} cells={monthCells} eventsByDay={eventsByDay}
                           dowHeaders={dowHeaders}
                           onOpenEvent={(ev) => setDetailsId(ev.id)}
                           onCreateOn={(yKey) => openCreate(yKey)} />
            )}

            <Legend />

            {/* Details modal */}
            <Modal open={!!detailsEvent} onClose={() => setDetailsId(null)}
                   title={detailsEvent?.title || "Event"} size="xl">
                {detailsEvent && (
                    <EventDetails
                        ev={detailsEvent}
                        fmt={fmt}
                        onEdit={() => openEdit(detailsEvent)}
                        onDelete={() => remove(detailsEvent.id)}
                        onDuplicate={() => duplicate(detailsEvent)}
                        onToggleComplete={() => toggleComplete(detailsEvent)}
                    />
                )}
            </Modal>

            {/* Create / edit modal */}
            <Modal open={editorOpen} onClose={() => setEditorOpen(false)}
                   title={form.id ? "Edit event" : "Add event"} size="2xl">
                <EventForm form={form} setForm={setForm} onSubmit={submit}
                           onCancel={() => setEditorOpen(false)} />
            </Modal>
        </div>
    );
}

/* ------------------------------------------------------------------ subviews */
function Legend() {
    return (
        <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-[var(--text-ink)]/65" data-testid="cal-legend">
            {CATEGORIES.map(c => (
                <span key={c.key} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} /> {c.label}
                </span>
            ))}
        </div>
    );
}

function AgendaView({ allItems, fmt, onOpen }) {
    return (
        <div className="card-warm p-4 space-y-2" data-testid="cal-agenda">
            {allItems.length === 0 && <div className="text-sm text-[var(--text-ink)]/55">No events.</div>}
            {allItems.slice(0, 100).map(ev => {
                const color = eventColor(ev);
                const startD = ev.start ? new Date(ev.start) : null;
                const endD = ev.end ? new Date(ev.end) : null;
                const isAss = ev.id.startsWith("ass-");
                return (
                    <button key={ev.id}
                            onClick={() => isAss ? null : onOpen(ev)}
                            disabled={isAss}
                            data-testid={`agenda-event-${ev.id}`}
                            className="w-full text-left flex items-center gap-3 p-2.5 rounded-xl hover:bg-[rgba(123,45,142,0.06)] transition disabled:cursor-default">
                        <span className="w-1 self-stretch rounded-full" style={{ background: color }} />
                        <div className="flex-1 min-w-0">
                            <div className={`font-medium truncate ${ev.status === "complete" ? "line-through text-[var(--text-ink)]/50" : ""}`}>{ev.title}</div>
                            <div className="text-xs text-[var(--text-ink)]/60 flex items-center gap-2 flex-wrap">
                                {startD && <span>{fmt.fmtDate(startD)} · {fmt.fmtTime(startD)}</span>}
                                {endD && ev.end !== ev.start && <span>→ {fmt.fmtDate(endD)} · {fmt.fmtTime(endD)}</span>}
                                {ev.location && <span className="inline-flex items-center gap-0.5"><MapPin size={10}/> {ev.location}</span>}
                            </div>
                        </div>
                        {isAss
                            ? <Link to={ev.link_to} className="text-xs text-[var(--brand-purple)] hover:underline" onClick={(e) => e.stopPropagation()}>open</Link>
                            : <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                                    style={{ background: `${color}22`, color }}>{CATEGORY_BY_KEY[ev.category]?.label || ev.kind}</span>}
                    </button>
                );
            })}
        </div>
    );
}

function MonthView({ cursor, cells, eventsByDay, dowHeaders, onOpenEvent, onCreateOn }) {
    const todayKey = ymdLocal(new Date());
    return (
        <div className="card-warm p-3" data-testid="cal-month">
            <div className="grid grid-cols-7 text-[11px] uppercase text-[var(--text-ink)]/55 mb-1">
                {dowHeaders.map(d => <div key={d} className="p-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                    const inMonth = d.getMonth() === cursor.getMonth();
                    const key = ymdLocal(d);
                    const items = eventsByDay[key] || [];
                    const isToday = key === todayKey;
                    return (
                        <div key={`m-${key}-${i}`}
                             data-testid={`cal-cell-${key}`}
                             className={`min-h-[110px] border rounded-lg p-1.5 text-xs flex flex-col gap-0.5 ${isToday ? "border-[var(--brand-orange)] bg-[rgba(255,107,53,0.06)]" : "border-[rgba(26,19,24,0.06)]"} ${inMonth ? "" : "opacity-50"}`}>
                            <div className="flex items-center justify-between">
                                <span className={`text-[11px] ${isToday ? "text-[var(--brand-orange)] font-semibold" : ""}`}>{d.getDate()}</span>
                                <button onClick={() => onCreateOn(key)}
                                        title="Quick add"
                                        data-testid={`cal-add-${key}`}
                                        className="text-[var(--text-ink)]/30 hover:text-[var(--brand-purple)]">
                                    <Plus size={10}/>
                                </button>
                            </div>
                            <div className="flex flex-col gap-0.5 overflow-hidden">
                                {items.slice(0, 4).map(ev => (
                                    <EventChip key={`${ev.id}-${key}`} ev={ev}
                                               onClick={() => onOpenEvent(ev)} />
                                ))}
                                {items.length > 4 && (
                                    <div className="text-[10px] text-[var(--text-ink)]/55 pl-1">+{items.length - 4} more</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function WeekView({ days, eventsByDay, dowHeaders, fmt, onOpenEvent, onCreateOn }) {
    const todayKey = ymdLocal(new Date());
    return (
        <div className="card-warm p-3" data-testid="cal-week">
            <div className="grid grid-cols-7 gap-1 mb-1 text-[11px] uppercase text-[var(--text-ink)]/55">
                {days.map((d, i) => (
                    <div key={`h-${ymdLocal(d)}-${i}`} className="p-1 flex items-baseline gap-1">
                        <span>{dowHeaders[i]}</span>
                        <span className="text-base text-[var(--text-ink)]">{d.getDate()}</span>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                    const key = ymdLocal(d);
                    const items = eventsByDay[key] || [];
                    return (
                        <div key={`w-${key}-${i}`}
                             data-testid={`cal-wcell-${key}`}
                             className={`min-h-[280px] border rounded-lg p-1.5 flex flex-col gap-1 ${key === todayKey ? "border-[var(--brand-orange)] bg-[rgba(255,107,53,0.06)]" : "border-[rgba(26,19,24,0.06)]"}`}>
                            {items.length === 0 && (
                                <button onClick={() => onCreateOn(key)} className="text-[10px] text-[var(--text-ink)]/35 hover:text-[var(--brand-purple)] py-1" data-testid={`week-add-${key}`}>+ add</button>
                            )}
                            {items.map(ev => (
                                <EventChip key={`${ev.id}-${key}`} ev={ev}
                                           onClick={() => onOpenEvent(ev)}
                                           timeLabel={ev.start && ev._segment !== "mid" && ev._segment !== "last" ? fmt.fmtTime(new Date(ev.start)) : ""} />
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function EventChip({ ev, onClick, timeLabel = "" }) {
    const color = eventColor(ev);
    const seg = ev._segment || "only";
    const rounded =
        seg === "only" ? "rounded-md"
      : seg === "first" ? "rounded-l-md rounded-r-none"
      : seg === "last"  ? "rounded-r-md rounded-l-none"
      : "rounded-none";
    const showTitle = seg === "first" || seg === "only";
    // Category label — surface the name so users don't have to memorise colours.
    // Assessments/classes carry a `kind` instead of `category`; fall back to that.
    const catLabel = CATEGORY_BY_KEY[ev.category]?.label
        || (ev.kind ? ev.kind.charAt(0).toUpperCase() + ev.kind.slice(1) : null);
    return (
        <button onClick={(e) => { e.stopPropagation(); onClick?.(); }}
                data-testid={`cal-event-${ev.id}`}
                title={catLabel ? `${catLabel} · ${ev.title}` : ev.title}
                style={{ background: `${color}22`, color, borderLeft: seg === "first" || seg === "only" ? `3px solid ${color}` : "none" }}
                className={`text-left text-[10px] px-1.5 py-0.5 ${rounded} truncate hover:brightness-95 ${ev.status === "complete" ? "line-through opacity-60" : ""}`}>
            {showTitle ? (
                <>
                    {timeLabel && <span className="font-medium mr-1 tabular-nums">{timeLabel}</span>}
                    {catLabel && (
                        <span
                            className="hidden md:inline-block text-[9px] font-semibold uppercase tracking-wide mr-1 opacity-80"
                            data-testid={`cal-event-cat-${ev.id}`}
                        >{catLabel}</span>
                    )}
                    <span className="truncate">{ev.title}</span>
                </>
            ) : (
                <span className="opacity-70 truncate">{ev.title}</span>
            )}
        </button>
    );
}

/* Details modal body */
function EventDetails({ ev, fmt, onEdit, onDelete, onDuplicate, onToggleComplete }) {
    const isAss = ev.id?.startsWith("ass-");
    const color = eventColor(ev);
    const startD = ev.start ? new Date(ev.start) : null;
    const endD = ev.end ? new Date(ev.end) : null;
    const sameDay = startD && endD && ymdLocal(startD) === ymdLocal(endD);
    const cat = CATEGORY_BY_KEY[ev.category] || CATEGORY_BY_KEY.appointment;

    return (
        <div className="space-y-4" data-testid="event-details">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${color}22`, color }} data-testid="event-details-category">
                    <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                    {cat.label}
                </span>
                {ev.status === "complete" && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[var(--status-green)]/15 text-[var(--status-green)]" data-testid="event-details-completed">Completed</span>}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <DetailRow icon={CalIcon} label="Starts">
                    {startD ? `${fmt.fmtDate(startD)} · ${fmt.fmtTime(startD)}` : "—"}
                </DetailRow>
                <DetailRow icon={CalIcon} label="Ends">
                    {endD ? (sameDay ? fmt.fmtTime(endD) : `${fmt.fmtDate(endD)} · ${fmt.fmtTime(endD)}`) : "—"}
                </DetailRow>
                {ev.location && (
                    <DetailRow icon={MapPin} label="Location">{ev.location}</DetailRow>
                )}
            </div>

            {ev.description && (
                <DetailBlock icon={AlignLeft} label="Description" testid="event-details-description">
                    {ev.description}
                </DetailBlock>
            )}
            {ev.notes && (
                <DetailBlock icon={AlignLeft} label="Notes" testid="event-details-notes">
                    {ev.notes}
                </DetailBlock>
            )}

            {!isAss && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-[rgba(26,19,24,0.08)]">
                    <button onClick={onToggleComplete} data-testid="event-action-complete"
                            className={`btn-ghost text-sm inline-flex items-center gap-1.5 ${ev.status === "complete" ? "text-[var(--status-green)]" : ""}`}>
                        <CheckCircle2 size={14}/> {ev.status === "complete" ? "Mark active" : "Mark complete"}
                    </button>
                    <button onClick={onDuplicate} data-testid="event-action-duplicate"
                            className="btn-ghost text-sm inline-flex items-center gap-1.5"><Copy size={14}/> Duplicate</button>
                    <button onClick={onEdit} data-testid="event-action-edit"
                            className="btn-ghost text-sm inline-flex items-center gap-1.5"><Pencil size={14}/> Edit</button>
                    <button onClick={onDelete} data-testid="event-action-delete"
                            className="btn-ghost text-sm inline-flex items-center gap-1.5 text-rose-700"><Trash2 size={14}/> Delete</button>
                </div>
            )}
        </div>
    );
}

function DetailRow({ icon: Icon, label, children }) {
    return (
        <div className="flex items-start gap-2 text-sm">
            <Icon size={14} className="text-[var(--text-ink)]/45 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">{label}</div>
                <div>{children}</div>
            </div>
        </div>
    );
}
function DetailBlock({ icon: Icon, label, testid, children }) {
    return (
        <div className="rounded-xl bg-[rgba(26,19,24,0.04)] p-3" data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55 mb-1">
                <Icon size={12} aria-hidden /> {label}
            </div>
            <div className="text-sm whitespace-pre-wrap">{children}</div>
        </div>
    );
}

/* Create / edit form */
function EventForm({ form, setForm, onSubmit, onCancel }) {
    const isAllDay = !!form.all_day;
    return (
        <form onSubmit={onSubmit} className="grid gap-3" data-testid="event-form">
            <input required placeholder="Title" value={form.title}
                   onChange={(e) => setForm({ ...form, title: e.target.value })}
                   data-testid="event-input-title"
                   className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />

            <div className="grid sm:grid-cols-2 gap-2">
                {isAllDay ? (
                    <>
                        <DatePicker value={(form.start || "").slice(0, 10)}
                                    onChange={(v) => setForm({ ...form, start: v ? `${v}T00:00` : "" })}
                                    testid="event-input-startdate" placeholder="Start date" />
                        <DatePicker value={(form.end || "").slice(0, 10)}
                                    onChange={(v) => setForm({ ...form, end: v ? `${v}T23:59` : "" })}
                                    testid="event-input-enddate" placeholder="End date (optional)" />
                    </>
                ) : (
                    <>
                        <DateTimePicker value={form.start}
                                        onChange={(v) => setForm({ ...form, start: v })}
                                        testid="event-input-start" placeholder="Start (date & time)" />
                        <DateTimePicker value={form.end}
                                        onChange={(v) => setForm({ ...form, end: v })}
                                        testid="event-input-end" placeholder="End (optional)" />
                    </>
                )}
            </div>
            <label className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={isAllDay}
                       onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                       data-testid="event-input-allday" />
                All-day event
            </label>

            <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-sm">Category
                    <select value={form.category || "appointment"}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            data-testid="event-input-category"
                            className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                        {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.dot} {c.label}</option>)}
                    </select>
                </label>
                <label className="text-sm">Location
                    <input value={form.location || ""}
                           onChange={(e) => setForm({ ...form, location: e.target.value })}
                           data-testid="event-input-location"
                           placeholder="Optional"
                           className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                </label>
            </div>

            <div>
                <div className="text-sm inline-flex items-center gap-1.5 mb-1"><Palette size={12}/> Colour</div>
                <div className="flex gap-1.5">
                    <button type="button"
                            onClick={() => setForm({ ...form, color: "" })}
                            title="Use category colour"
                            data-testid="event-color-default"
                            className={`w-6 h-6 rounded-full border ${!form.color ? "ring-2 ring-[var(--brand-purple)] ring-offset-1" : "border-[rgba(26,19,24,0.15)]"} bg-white inline-flex items-center justify-center`}>
                        <X size={10} className="text-[var(--text-ink)]/40" />
                    </button>
                    {PALETTE.map(c => (
                        <button type="button" key={c}
                                onClick={() => setForm({ ...form, color: c })}
                                style={{ background: c }}
                                title={c}
                                data-testid={`event-color-${c.replace("#", "")}`}
                                className={`w-6 h-6 rounded-full ${form.color === c ? "ring-2 ring-[var(--brand-purple)] ring-offset-1" : ""}`} />
                    ))}
                </div>
            </div>

            <label className="text-sm">Description
                <textarea rows={2} value={form.description || ""}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          data-testid="event-input-description"
                          placeholder="What is this event about?"
                          className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
            </label>
            <label className="text-sm">Notes
                <textarea rows={3} value={form.notes || ""}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          data-testid="event-input-notes"
                          placeholder="Private notes — visible after saving"
                          className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
            </label>

            <ReminderEditor value={form.reminders || []}
                            onChange={(reminders) => setForm({ ...form, reminders })} />

            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="event-save-btn" className="btn-primary">
                    {form.id ? "Save changes" : "Add event"}
                </button>
            </div>
        </form>
    );
}
